import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  effect,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import moment from 'moment';
import { Select2ScrollEvent, Select2SearchEvent } from 'ng-select2-component';
import {
  IBase,
  FilterListKey,
  IFilterCheckboxOption,
  IFilterPanelColumn,
  IFilterPanelSelection,
  IFilterPanelValues,
  IFilterReceiversValue,
  IFilterSelectOption,
  ISuggestionIcon,
  ITown,
} from 'src/app/models';
import {
  CommunicationUserType,
  ExpeditionStatusGroup,
  FilterField,
  FilterFieldKind,
  FilterType,
  InputType,
  SuggestionVisibility,
} from 'src/app/models/enums';
import { APP } from 'src/app/config/app';
import {
  FilterOptionsService,
  RouteService,
  StorageService,
  TripsService,
  UsersService,
  UtilsService,
  VehiclesService,
} from 'src/app/services';
import { getExpeditionOptions, suggestionExpeditionOption } from 'src/app/forms/expeditionOptions';
import {
  suggestionPriorityIcon,
  suggestionStatusIcon,
  suggestionVisibilityIcon,
  toSuggestionExpedition,
} from 'src/app/utils/suggestionTickets';
import { FILTER_PANEL_ADAPTERS } from './filterPanelAdapters';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { ServiceInfoService } from 'src/app/services/service-info/service-info.service';
import { FilterOptionList } from './filterOptionList';
import {
  driverLoader,
  driverOption,
  routeLoader,
  routeOption,
  serviceLoader,
  serviceOption,
  vehicleLoader,
  vehicleOption,
} from './filterOptionSources';
import { FilterReceiversComponent } from './filter-receivers/filter-receivers.component';
import { TooltipPosition } from '../tooltip/tooltip-position';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { TooltipModule } from '../tooltip/tooltip.module';
import { CalendarComponent } from '../input/calendar/calendar.component';
import { CheckboxComponent } from '../input/checkbox/checkbox.component';
import { InputComponent } from '../input/input.component';
import { MultiSelectFormComponent } from '../input/multi-select-group/multi-select-form/multi-select-form.component';
@Component({
  selector: 'app-filter-panel',
  templateUrl: './filter-panel.component.html',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TooltipModule,
    CalendarComponent,
    CheckboxComponent,
    FilterReceiversComponent,
    InputComponent,
    MultiSelectFormComponent,
  ],
  styleUrls: ['./filter-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class FilterPanelComponent {
  readonly fields = input<FilterField[]>([]);
  readonly filterType = input.required<FilterType>();
  readonly selection = input<IFilterPanelSelection | null>(null);
  readonly communicationUserType = input<CommunicationUserType>();
  readonly componentOpen = input<string>();
  readonly pastDatesOnly = input(false);

  readonly receiversComponent = viewChild(FilterReceiversComponent);

  readonly filter = output<IFilterPanelValues>();
  readonly closeFilter = output<void>();

  readonly FilterField = FilterField;
  readonly FilterFieldKind = FilterFieldKind;
  readonly InputType = InputType;
  readonly TooltipPosition = TooltipPosition;
  readonly definitions = APP.FILTER_FIELDS;

  clients: IBase[] = [];
  areas: IBase[] = [];
  private allAreas: IBase[] = [];

  operators: IBase[] = [];
  private accessibleOperatorsCount = 0;
  senders: IBase[] = [];

  selectedClients: IBase[] = [];
  selectedAreas: IBase[] = [];
  selectedOperators: IBase[] = [];
  selectedSenders: IBase[] = [];
  receiversSelection: IFilterReceiversValue | null = null;

  origin = '';
  destination = '';
  from?: Date;
  to?: Date;
  startTime = '';
  endTime = '';

  checkboxes: Partial<Record<FilterField, IFilterCheckboxOption[]>> = {};
  readonly optionElements = signal<Partial<Record<FilterField, IFilterSelectOption[]>>>({});
  readonly optionSelections = signal<Partial<Record<FilterField, IFilterSelectOption[]>>>({});
  readonly columns = signal<IFilterPanelColumn[]>([]);

  lists: Partial<Record<FilterListKey, FilterOptionList>> = {};

  constructor(
    public literalService: LiteralService,
    private usersService: UsersService,
    private serviceInfoService: ServiceInfoService,
    private routeService: RouteService,
    private vehiclesService: VehiclesService,
    private storageService: StorageService,
    private filterOptionsService: FilterOptionsService,
    private tripsService: TripsService,
    private utilsService: UtilsService,
    private elementRef: ElementRef<HTMLElement>,
  ) {
    effect(() => {
      // the parent may hand over a fresh array on every check, so only a real change rebuilds
      const optionsKey = [this.filterType(), this.fields().join('|'), this.communicationUserType()].join('|');
      const selectionKey = JSON.stringify(this.selection());
      if (optionsKey === this.optionsKey && selectionKey === this.selectionKey) return;
      const optionsChanged = optionsKey !== this.optionsKey;
      this.optionsKey = optionsKey;
      this.selectionKey = selectionKey;
      untracked(() => (optionsChanged ? this.setup() : this.applySelection()));
    });
  }

  @HostListener('window:click', ['$event'])
  onWindowClick(event: Event) {
    const toggle = this.componentOpen() ? document.getElementById(this.componentOpen()!) : null;
    const panel = this.elementRef.nativeElement;
    if (!toggle || panel.closest('.hidden')) return;
    if (this.utilsService.closeCustomModals(event, panel, toggle)) this.closeFilter.emit();
  }

  private optionsKey?: string;
  private selectionKey?: string;
  private optionsLoaded: Promise<void> = Promise.resolve();

  private async setup() {
    this.checkboxes = {};
    this.buildCheckboxes();
    this.optionsLoaded = this.loadOptions();
    await this.applySelection();
  }

  private async loadOptions() {
    this.lists = {};
    this.buildLists();
    this.refreshColumns();
    await this.loadClientsAndAreas();
    this.refreshColumns();
    await Promise.all([
      ...Object.values(this.lists).map((list) => list!.reload()),
      this.shows(FilterField.OPERATORS) ? this.loadAccessibleOperators() : Promise.resolve(),
      this.shows(FilterField.SENDERS) ? this.loadSenders() : Promise.resolve(),
    ]);
  }

  private async applySelection() {
    await this.optionsLoaded;
    this.from = undefined;
    this.to = undefined;
    this.startTime = '';
    this.endTime = '';
    this.selectedStatusGroup = undefined;
    this.selectedStatuses = [];
    this.selectedSenders = [];
    this.buildCheckboxes();
    this.clearListSelections();
    await this.restoreSelection();
    this.syncOptionSelections();
    this.selectDefaultStatusGroup();
  }

  private clearListSelections() {
    Object.entries(this.lists).forEach(([key, list]) => {
      if (!APP.FILTER_SERVICE_SCOPED_LISTS.includes(key as FilterListKey)) return list!.selectIds([]);
      list!.clear();
      list!.disabled = true;
    });
  }

  private selectDefaultStatusGroup() {
    if (!this.shows(FilterField.EXPEDITION_STATUS) || this.selectedStatusGroup) return;
    if (this.statusGroupsOfType()[ExpeditionStatusGroup.PENDING]) {
      this.onStatusGroupChange(ExpeditionStatusGroup.PENDING);
    }
  }

  private get adapter() {
    return FILTER_PANEL_ADAPTERS[this.filterType()];
  }

  private async storedSelection(): Promise<IFilterPanelSelection | null> {
    const stored = await this.adapter?.read?.(this.storageService);
    return stored ? this.adapter!.toSelection(stored) : null;
  }

  private async restoreSelection() {
    const selection = this.selection() ?? (await this.storedSelection());
    if (!selection) return;
    this.selectedClients = this.clients.filter((client) => selection.ids[FilterField.CLIENTS]?.includes(client.id!));
    this.selectedAreas = this.areas.filter((area) => selection.ids[FilterField.AREAS]?.includes(area.id!));
    if (this.shows(FilterField.OPERATORS)) await this.loadOperators();
    this.selectedOperators = this.operators.filter((operator) =>
      selection.ids[FilterField.OPERATORS]?.includes(operator.id!),
    );
    this.selectedSenders = this.senders.filter((sender) => selection.ids[FilterField.SENDERS]?.includes(sender.id!));
    this.receiversSelection = selection.receivers ?? null;
    const ids = selection.ids as Record<string, (number | string)[]>;
    Object.entries(ids).forEach(([field, fieldIds]) => {
      if (!APP.FILTER_SERVICE_SCOPED_LISTS.includes(field as FilterListKey)) this.lists[field as FilterField]?.selectIds(fieldIds ?? []);
    });
    await Promise.all(APP.FILTER_SERVICE_SCOPED_LISTS.map((key) => this.restoreServiceScoped(key, ids[key] ?? [])));
    Object.entries(selection.checked ?? {}).forEach(([field, values]) => {
      this.checkboxes[field as FilterField]?.forEach((option) => (option.checked = !!values?.includes(option.value)));
    });
    this.restoreStatusGroup(selection.checked?.[FilterField.EXPEDITION_STATUS] ?? []);
    this.origin = selection.origin ?? this.origin;
    this.destination = selection.destination ?? this.destination;
    this.from = selection.from ?? this.from;
    this.to = selection.to ?? this.to;
    this.startTime = selection.startTime ?? this.startTime;
    this.endTime = selection.endTime ?? this.endTime;
  }

  private async restoreServiceScoped(key: FilterListKey, ids: (number | string)[]) {
    const list = this.lists[key];
    if (!list || !this.lists['SERVICES']?.selected.length) return;
    list.disabled = false;
    await list.reload();
    list.selectIds(ids);
  }

  private buildCheckboxes() {
    const optionElements: Partial<Record<FilterField, IFilterSelectOption[]>> = {};
    this.fields()
      .filter((field) => APP.FILTER_OPTION_KINDS.includes(this.definitions[field].kind))
      .forEach((field) => {
        this.checkboxes[field] = this.checkboxOptions(field).map((value) => ({
          value,
          checked: !!this.definitions[field].defaultChecked,
        }));
        if (this.definitions[field].kind !== FilterFieldKind.OPTION_MULTISELECT) return;
        optionElements[field] = this.checkboxOptions(field).map((value) => ({
          id: value,
          name: this.optionLabel(field, value),
        }));
      });
    this.optionElements.set(optionElements);
    this.syncOptionSelections();
  }

  private checkboxOptions(field: FilterField): readonly string[] {
    if (field === FilterField.SUGGESTION_TYPES && this.communicationUserType()) {
      return APP.SUGGESTION_TYPE_OPTIONS[this.communicationUserType()!];
    }
    return APP.FILTER_CHECKBOX_OPTIONS[field] ?? [];
  }

  private buildLists() {
    if (this.showsServices()) {
      this.lists['SERVICES'] = new FilterOptionList(
        serviceLoader(this.serviceInfoService, () => this.serviceScope()),
        serviceOption,
      );
    }
    if (this.fields().some((field) => this.definitions[field].showsRoutes)) {
      this.lists['ROUTES'] = new FilterOptionList(
        routeLoader(this.routeService, () => this.lists['SERVICES']?.selected.map((option) => option.id) ?? []),
        routeOption,
      );
      this.lists['ROUTES']!.disabled = true;
    }
    if (this.shows(FilterField.AREAS_SERVICES_EXPEDITIONS)) {
      this.lists['EXPEDITIONS'] = new FilterOptionList(
        async (page, search) => {
          const services = this.lists['SERVICES']?.selected.map((option) => ({ id: option.id })) ?? [];
          if (!services.length) return { content: [], last: true };
          const size = FilterOptionList.pageSize();
          const content = await getExpeditionOptions(this.tripsService, { services, page, size, search }, toSuggestionExpedition);
          return { content, last: content.length < size };
        },
        suggestionExpeditionOption,
      );
      this.lists['EXPEDITIONS']!.disabled = true;
    }
    if (this.shows(FilterField.VEHICLES)) {
      this.lists[FilterField.VEHICLES] = new FilterOptionList(vehicleLoader(this.vehiclesService, () => []), vehicleOption);
    }
    if (this.shows(FilterField.DRIVERS)) {
      this.lists[FilterField.DRIVERS] = new FilterOptionList(
        driverLoader(this.usersService, () => this.selectedClientIds()),
        driverOption,
      );
    }
  }

  private async loadClientsAndAreas() {
    const needed =
      this.shows(FilterField.CLIENTS) || this.shows(FilterField.AREAS) || this.showsServices();
    if (!needed) return;
    const { clients, towns } = await this.filterOptionsService.getPossibleClientTowns();
    this.clients = (clients ?? []).map((client: any) => ({ id: client.id, name: client.name }));
    this.allAreas = (towns as ITown[]).map((town) => ({ id: town.id, name: town.name }));
    this.areas = [...this.allAreas];
  }

  private async loadAccessibleOperators() {
    const operators = await this.filterOptionsService.getOperators();
    this.accessibleOperatorsCount = operators.length;
    this.setOperators(operators);
    this.refreshColumns();
  }

  private async loadOperators() {
    const clientIds = this.selectedClientIds();
    if (!clientIds.length) return this.loadAccessibleOperators();
    this.setOperators((await this.filterOptionsService.getOperatorsByClient(clientIds)) as IBase[]);
  }

  private setOperators(operators: IBase[]) {
    this.operators = (operators ?? []).map((operator: IBase) => ({ id: operator.id, name: operator.name }));
    this.selectedOperators = this.selectedOperators.filter((selected) =>
      this.operators.some((operator) => operator.id === selected.id),
    );
  }

  private async loadSenders() {
    const senders = (await this.filterOptionsService.getSenders(this.communicationUserType()!)) as any[];
    this.senders = (senders ?? []).map((sender) => ({
      id: sender.id,
      code: `${sender.firstName} ${sender.lastName}`,
      name: `${sender.firstName} ${sender.lastName}`,
    }));
  }

  // the areas the services are scoped to: the selected ones, or every area the user can reach
  private serviceScope(): ITown[] {
    const areas = this.selectedAreas.length ? this.selectedAreas : this.areas;
    return areas.map((area) => ({ id: area.id })) as ITown[];
  }

  private selectedClientIds(): number[] {
    return this.selectedClients.map((client) => client.id!).filter((id) => id != null);
  }

  shows(field: FilterField): boolean {
    return this.fields().includes(field);
  }

  showsServices(): boolean {
    return this.fields().some((field) => this.definitions[field].kind === FilterFieldKind.AREAS_SERVICES);
  }

  // a single client is no choice at all, so the selector only earns its column when there are several
  private showsField(field: FilterField): boolean {
    if (field === FilterField.CLIENTS) return this.shows(FilterField.CLIENTS) && this.clients.length > 1;
    // counted without selected clients, so the selector does not vanish while filtering
    if (field === FilterField.OPERATORS) return this.shows(FilterField.OPERATORS) && this.accessibleOperatorsCount > 1;
    return this.shows(field);
  }

  private refreshColumns() {
    const visibleFields = this.fields().filter((field) => this.showsField(field));
    const visibleFieldSet = new Set(visibleFields);
    const stackedByHost = new Map<FilterField, FilterField[]>();
    visibleFields.forEach((field) => {
      const host = this.definitions[field].stacksUnder;
      if (host === undefined || !visibleFieldSet.has(host)) return;
      stackedByHost.set(host, [...(stackedByHost.get(host) ?? []), field]);
    });
    const stackedFields = new Set([...stackedByHost.values()].flat());
    this.columns.set(
      visibleFields
        .filter((field) => !stackedFields.has(field))
        .map((field) => {
          const stacked = stackedByHost.get(field) ?? [];
          return { field, fields: [field, ...stacked], label: this.columnLabel(field, stacked) };
        }),
    );
  }

  private columnLabel(field: FilterField, stackedFields: FilterField[]): string {
    if (field === FilterField.DATE_RANGE) {
      return `${this.literalService.get('from', true)} - ${this.literalService.get('to', true)}`;
    }
    return [field, ...stackedFields]
      .map((columnField) => this.literalService.get(this.definitions[columnField].label, true))
      .join(' - ');
  }

  private syncOptionSelections() {
    const optionSelections: Partial<Record<FilterField, IFilterSelectOption[]>> = {};
    Object.entries(this.optionElements()).forEach(([field, elements]) => {
      const checkedValues = new Set(
        (this.checkboxes[field as FilterField] ?? []).filter((option) => option.checked).map((option) => option.value),
      );
      optionSelections[field as FilterField] = (elements ?? []).filter((element) => checkedValues.has(element.id));
    });
    this.optionSelections.set(optionSelections);
  }

  onOptionsChange(field: FilterField) {
    const selectedValues = new Set((this.optionSelections()[field] ?? []).map((option) => option.id));
    this.checkboxes[field]?.forEach((option) => (option.checked = selectedValues.has(option.value)));
  }

  fromDateLimit(): string | undefined {
    return this.from ? moment(this.from).format('DD/MM/YYYY') : undefined;
  }

  pastDateLimit(date?: Date): string | undefined {
    if (!this.pastDatesOnly()) return undefined;
    return moment(date ?? new Date()).format('DD/MM/YYYY');
  }

  fromDateText(): string {
    return this.from ? moment(this.from).format('YYYY-MM-DD') : '';
  }

  toDateText(): string {
    return this.to ? moment(this.to).format('YYYY-MM-DD') : '';
  }

  async onClientsClose() {
    const clientIds = this.selectedClientIds();
    if (this.shows(FilterField.AREAS) || this.showsServices()) {
      this.areas = clientIds.length
        ? ((await this.filterOptionsService.getTownsByClient(clientIds)) as ITown[]).map((town) => ({
            id: town.id,
            name: town.name,
          }))
        : [...this.allAreas];
      this.selectedAreas = this.selectedAreas.filter((area) => this.areas.some((option) => option.id === area.id));
      await this.onAreasClose();
    }
    if (this.shows(FilterField.OPERATORS)) await this.loadOperators();
  }

  async onAreasClose() {
    this.lists['SERVICES']?.clear();
    APP.FILTER_SERVICE_SCOPED_LISTS.forEach((key) => {
      const list = this.lists[key];
      if (!list) return;
      list.clear();
      list.disabled = true;
    });
    await this.lists['SERVICES']?.reload();
  }

  onServicesChange(selected: any[]) {
    const services = this.lists['SERVICES'];
    if (!services) return;
    services.selectValues(selected);
    APP.FILTER_SERVICE_SCOPED_LISTS.forEach((key) => {
      const list = this.lists[key];
      if (!list) return;
      list.clear();
      list.disabled = !services.selected.length;
      if (services.selected.length) list.reload();
    });
  }

  onSelectionChange(key: FilterListKey, selected: any[]) {
    if (key === 'SERVICES') {
      this.onServicesChange(selected);
      return;
    }
    this.lists[key]?.selectValues(selected);
  }

  async onScroll(event: Select2ScrollEvent, key: FilterListKey) {
    if (event.way !== 'down') return;
    await this.lists[key]?.loadMore();
  }

  async onSearch(event: Select2SearchEvent, key: FilterListKey) {
    await this.lists[key]?.searchFor(event.search);
  }

  changeDate(event: any, type: 'from' | 'to') {
    const parsed = moment(event?.target?.value, 'DD/MM/YYYY');
    const date = parsed.isValid() ? parsed.toDate() : undefined;
    if (type === 'from') this.from = date;
    else this.to = date;
  }

  changeTime(event: any, type: 'start' | 'end') {
    const time = event?.target?.value ?? '';
    if (type === 'start') this.startTime = time;
    else this.endTime = time;
  }

  selectedStatusGroup?: ExpeditionStatusGroup;
  selectedStatuses: string[] = [];

  private statusGroupsOfType() {
    return APP.EXPEDITION_STATUS_GROUPS[this.filterType()] ?? {};
  }

  statusGroups(): ExpeditionStatusGroup[] {
    return Object.keys(this.statusGroupsOfType()) as ExpeditionStatusGroup[];
  }

  groupStatuses(group: ExpeditionStatusGroup): string[] {
    const statuses = this.statusGroupsOfType()[group]?.statuses ?? [];
    return statuses.length > 1 ? statuses : [];
  }

  allowsManyStatuses(group: ExpeditionStatusGroup): boolean {
    return !!this.statusGroupsOfType()[group]?.multiple;
  }

  private restoreStatusGroup(statuses: string[]) {
    if (!statuses.length) return;
    const groups = this.statusGroupsOfType();
    const group = (Object.keys(groups) as ExpeditionStatusGroup[]).find((key) =>
      statuses.some((status) => groups[key]!.statuses.includes(status)),
    );
    if (!group) return;
    this.selectedStatusGroup = group;
    this.selectedStatuses = statuses.filter((status) => groups[group]!.statuses.includes(status));
  }

  onStatusGroupChange(group: ExpeditionStatusGroup) {
    this.selectedStatusGroup = group;
    const definition = this.statusGroupsOfType()[group];
    if (!definition) return;
    this.selectedStatuses = definition.multiple ? [...definition.statuses] : definition.statuses.slice(0, 1);
  }

  onStatusChange(group: ExpeditionStatusGroup, status: string) {
    if (!this.allowsManyStatuses(group)) {
      this.selectedStatuses = [status];
      return;
    }
    this.selectedStatuses = this.selectedStatuses.includes(status)
      ? this.selectedStatuses.filter((selected) => selected !== status)
      : [...this.selectedStatuses, status];
  }

  isStatusChecked(status: string): boolean {
    return this.selectedStatuses.includes(status);
  }

  optionLabel(field: FilterField, value: string): string {
    const definition = this.definitions[field];
    const key = definition.rawOptionLabels ? value : value.toLowerCase();
    return this.literalService.get(`${definition.optionLabelPrefix ?? ''}${key}`, true);
  }

  optionIcon(field: FilterField, value: string): ISuggestionIcon | null {
    switch (field) {
      case FilterField.SUGGESTION_PRIORITIES:
        return suggestionPriorityIcon(value);
      case FilterField.SUGGESTION_STATUS:
        return suggestionStatusIcon(value);
      case FilterField.SUGGESTION_VISIBILITIES:
        return { src: suggestionVisibilityIcon(value as SuggestionVisibility), filterClass: 'filter-primary-purple' };
      default:
        return null;
    }
  }

  optionTooltip(field: FilterField, value: string): string {
    const prefix = this.definitions[field].optionTooltipPrefix;
    return prefix ? this.literalService.get(`${prefix}${value}`, true) : '';
  }

  onCheckboxChange(field: FilterField, value: string) {
    const option = this.checkboxes[field]?.find((checkbox) => checkbox.value === value);
    if (option) option.checked = !option.checked;
  }

  clearSelection() {
    this.selectedClients = [];
    this.selectedAreas = [];
    this.selectedOperators = [];
    this.selectedSenders = [];
    this.receiversComponent()?.clear();
    this.origin = '';
    this.destination = '';
    this.from = undefined;
    this.to = undefined;
    this.startTime = '';
    this.endTime = '';
    this.selectedStatusGroup = undefined;
    this.selectedStatuses = [];
    this.selectDefaultStatusGroup();
    Object.entries(this.checkboxes).forEach(([field, options]) =>
      options?.forEach((option) => (option.checked = !!this.definitions[field as FilterField].defaultChecked)),
    );
    this.syncOptionSelections();
    Object.values(this.lists).forEach((list) => list?.clear());
    this.areas = [...this.allAreas];
    if (this.shows(FilterField.OPERATORS)) this.loadAccessibleOperators();
    this.onAreasClose();
  }

  onSubmit() {
    const values = this.buildValues();
    const adapter = this.adapter;
    if (adapter) adapter.write?.(this.storageService, adapter.toFilters(values));
    this.filter.emit(values);
    this.closeFilter.emit();
  }

  private buildValues(): IFilterPanelValues {
    const checkboxes: Partial<Record<FilterField, string[]>> = {};
    Object.entries(this.checkboxes).forEach(([field, options]) => {
      checkboxes[field as FilterField] = (options ?? [])
        .filter((option) => option.checked)
        .map((option) => option.value);
    });
    if (this.shows(FilterField.EXPEDITION_STATUS)) {
      checkboxes[FilterField.EXPEDITION_STATUS] = [...this.selectedStatuses];
    }
    return {
      clients: this.selectedClients,
      areas: this.selectedAreas,
      services: this.selectedValues('SERVICES'),
      routes: this.selectedValues('ROUTES'),
      operators: this.selectedOperators,
      senders: this.selectedSenders,
      receivers: this.receiversComponent()?.value() ?? { receivers: [] },
      vehicles: this.selectedValues(FilterField.VEHICLES),
      drivers: this.selectedValues(FilterField.DRIVERS),
      expeditions: this.selectedValues('EXPEDITIONS'),
      origin: this.origin,
      destination: this.destination,
      from: this.from,
      to: this.to,
      startTime: this.startTime || undefined,
      endTime: this.endTime || undefined,
      checkboxes,
    };
  }

  private selectedValues(key: FilterListKey): IBase[] {
    return this.lists[key]?.values ?? [];
  }
}
